import React, { useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import SumsubWebSdk from '@sumsub/websdk-react'

import { BaseView, BaseText } from '@/components/base'
import { useTheme } from '@/hooks/useTheme'
import { identityApi } from '@/domain/identity/identityApi'
import { QUERY_KEYS } from '@/constants/queryKeys'
import { Close } from '@/assets/icons'
import useOffsets from '@/hooks/useOffsets'
import { useAppAnalytics } from '@/services/analytics'

interface SumsubWebSDKModalProps {
  isOpen: boolean
  onClose: () => void
  accessToken: string
}

// Sumsub theme configuration - matching RN version
const sumsubConfig = {
  lang: 'en',
  theme: 'dark',
  customCss: `
    :root {
      --black: #171819;
      --primary: #7866FF;
      --primary-hover: #645BCA;
      --secondary: #1A1A1A;
      --secondary-hover: #333333;
      --disabled: #AAAEB1;
      --disabled-text: #CCCCCC;
      --link: #7B91FF;
    }

    body {
      background-color: var(--black) !important;
      font-family: 'ARSRegionalR', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
    }

    .sumsub-button-primary {
      background-color: var(--primary) !important;
      color: #FFFFFF !important;
    }

    .sumsub-button-primary:hover {
      background-color: var(--primary-hover) !important;
    }

    .sumsub-button-primary:disabled {
      background-color: var(--disabled) !important;
      color: var(--disabled-text) !important;
    }

    .sumsub-button-secondary {
      background-color: var(--secondary) !important;
      color: #FFFFFF !important;
    }

    .sumsub-button-secondary:hover {
      background-color: var(--secondary-hover) !important;
    }

    a {
      color: var(--link) !important;
    }
  `,
}

export const SumsubWebSDKModal: React.FC<SumsubWebSDKModalProps> = ({
  isOpen,
  onClose,
  accessToken,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const offsets = useOffsets()
  const { trackEvent, events } = useAppAnalytics()
  // Store start time for time_to_complete calculation
  const startTime = React.useRef(Date.now())

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // Block body scroll
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const accessTokenExpirationHandler = useCallback(async () => {
    // Handler will be called when the token expires
    const { token: newToken } = await identityApi.getKycSdkAccessToken()
    return newToken || ''
  }, [])

  const messageHandler = useCallback(
    async (type: string, payload: any) => {
      // Handle different message types
      if (type === 'idCheck.onError') {
        trackEvent(events.KYC_ERROR, {
           error_type: typeof payload === 'string' ? payload : JSON.stringify(payload),
        })
        toast.error(t('api:errors.somethingWrong'))
      } else if (
        type === 'idCheck.applicantStatus' ||
        type === 'idCheck.onApplicantStatusChanged'
      ) {
        const status = payload?.reviewStatus || payload?.status

        if (status && status.toLowerCase() === 'completed') {
          // Refetch identity data and wait for it to complete
          // This ensures hasIdentityVerified is updated in the store before navigation
          await queryClient.refetchQueries({
            queryKey: [QUERY_KEYS.IDENTITY_ME],
          })

          const timeToComplete = (Date.now() - startTime.current) / 1000
          trackEvent(events.KYC_COMPLETED, {
            time_to_complete: Math.round(timeToComplete),
          })

          // Show success message
          // toast.success(
          //   t('translation:sumsubWebview.verificationCompletedSuccessfully'),
          // )

          // Close modal and navigate to kem-card screen
          onClose()
          // navigate('/kem-card', { replace: true })
        }
      }
    },
    [queryClient, navigate, t, onClose],
  )

  const errorHandler = useCallback(
    (error: any) => {
      trackEvent(events.KYC_ERROR, {
         error_type: typeof error === 'string' ? error : JSON.stringify(error),
      })
      toast.error(t('api:errors.somethingWrong'))
    },
    [t, trackEvent, events.KYC_ERROR],
  )

  if (!isOpen) return null

  const modalContent = (
    <BaseView
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        backgroundColor: '#1b1b1f', // theme.colors.background.screen || '#171819',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: `${offsets.top}px`,
        paddingBottom: `${offsets.bottom}px`,
      }}
      className="ym-hide-content"
    >
      {/* Header with close button */}
      <BaseView
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <BaseText fontSize={18} fontFamily="semibold" color="label.default">
          {t('translation:verifyIdentity')}
        </BaseText>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Close width={24} height={24} fill={theme.colors.label.default} />
        </button>
      </BaseView>

      {/* Sumsub SDK Container */}
      <BaseView
        style={{
          flex: 1,
          width: '100%',
          overflow: 'auto',
        }}
      >
        <SumsubWebSdk
          accessToken={accessToken}
          expirationHandler={accessTokenExpirationHandler}
          config={sumsubConfig}
          options={{ addViewportTag: false, adaptIframeHeight: true }}
          onMessage={messageHandler}
          onError={errorHandler}
        />
      </BaseView>
    </BaseView>
  )

  // Render in portal for correct z-index
  return createPortal(modalContent, document.body)
}
